import { ChartData } from 'chart.js'
import { FC, Fragment, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Card, CardContent, Typography, useTheme } from '@mui/material'

import { BarChart } from '../../../../../components/BarChart/BarChart'
import { AnalysisSuggestionsSummaryData } from '../../types/AnalysisSuggestion'

const formatCount = (value: number) => String(value)

export const AnalysisSuggestionsSummary: FC<{ summary: AnalysisSuggestionsSummaryData }> = ({ summary }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const unknownOrganization = t('internal-analysts:analysis_suggestions_unknown_organization')

  const chartData = useMemo<ChartData<'bar'>>(
    () => ({
      labels: summary.byOrganization.map(({ name }) => name),
      datasets: [
        {
          label: t('internal-analysts:analysis_suggestions_chart_series'),
          data: summary.byOrganization.map(({ count }) => count),
          yAxisID: 'y1',
          backgroundColor: theme.palette.primary.main,
        },
      ],
    }),
    [summary.byOrganization, t, theme.palette.primary.main]
  )

  const details = [
    { label: t('internal-analysts:analysis_suggestions_total'), value: summary.total },
    {
      label: t('internal-analysts:analysis_suggestions_organizations'),
      value: summary.organizationCount,
      extra:
        summary.topOrganization &&
        t('internal-analysts:analysis_suggestions_most_by', {
          name: summary.topOrganization.name ?? unknownOrganization,
          count: summary.topOrganization.count,
        }),
    },
    {
      label: t('internal-analysts:analysis_suggestions_users'),
      value: summary.userCount,
      extra:
        summary.topUser &&
        t('internal-analysts:analysis_suggestions_most_by', { name: summary.topUser.name ?? summary.topUser.email, count: summary.topUser.count }),
    },
    { label: t('internal-analysts:analysis_suggestions_average_importance'), value: summary.averageImportance },
  ]

  return (
    <Card data-testid="analysis-suggestions-summary">
      <CardContent>
        <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' }} gap={3} alignItems="center">
          <Box display="grid" gridTemplateColumns="auto 1fr" columnGap={2} rowGap={1.5} alignItems="baseline">
            {details.map(({ label, value, extra }) => (
              <Fragment key={label}>
                <Typography color="text.secondary">{label}</Typography>
                <Box minWidth={0}>
                  <Typography fontWeight="bold">{value}</Typography>
                  {extra && (
                    <Typography variant="body2" color="text.secondary">
                      {extra}
                    </Typography>
                  )}
                </Box>
              </Fragment>
            ))}
          </Box>
          <Box minWidth={0}>
            <Typography variant="subtitle2" color="text.secondary" textAlign="center">
              {t('internal-analysts:analysis_suggestions_chart_title')}
            </Typography>
            {summary.byOrganization.length ? (
              <BarChart data={chartData} valueFormatter={formatCount} scaleConfig={{ y1: { precision: 0 } }} />
            ) : (
              <Typography color="text.secondary" textAlign="center" py={4}>
                {t('internal-analysts:analysis_suggestions_chart_empty')}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}
